import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { useXeroIntegration } from '../../hooks/useXeroIntegration';

/**
 * XeroCallbackPage
 *
 * Handles the OAuth 2.0 redirect from Xero after user authorizes.
 * URL: /settings/xero/callback?code=xxx&state=yyy
 *
 * Exchanges the authorization code for tokens via the auth-xero Edge Function,
 * then redirects to OrgSettings → Integrations tab.
 */
export function XeroCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const xero = useXeroIntegration();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [errorMsg, setErrorMsg] = useState('');
  const processedRef = useRef(false);

  const processCallback = useCallback(async () => {
    // Prevent double-processing in React StrictMode
    if (processedRef.current) return;
    processedRef.current = true;

    const code = searchParams.get('code');
    const error = searchParams.get('error');

    if (error) {
      setStatus('error');
      setErrorMsg(
        error === 'access_denied'
          ? 'Access was denied. You need to authorize the app in Xero to continue.'
          : `Authorization failed: ${error}`,
      );
      return;
    }

    if (!code) {
      setStatus('error');
      setErrorMsg('No authorization code received from Xero.');
      return;
    }

    // Exchange code for tokens
    try {
      const success = await xero.handleCallback(code);
      if (success) {
        setStatus('success');
        // Auto-redirect after 2 seconds
        setTimeout(() => {
          navigate('/org-settings?tab=integrations', { replace: true });
        }, 2000);
      } else {
        setStatus('error');
        setErrorMsg('Failed to complete Xero authorization.');
      }
    } catch (err: unknown) {
      setStatus('error');
      setErrorMsg(err instanceof Error ? err.message : 'An unexpected error occurred.');
    }
  }, [searchParams, xero, navigate]);

  useEffect(() => {
    processCallback(); // eslint-disable-line react-hooks/set-state-in-effect
  }, [processCallback]);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'var(--bg-primary)',
        padding: 24,
      }}
    >
      <div
        style={{
          background: 'var(--bg-card)',
          border: '1px solid var(--border-color)',
          borderRadius: 16,
          padding: 48,
          maxWidth: 440,
          width: '100%',
          textAlign: 'center',
        }}
      >
        {/* Xero Logo */}
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: 16,
            background: 'linear-gradient(135deg, #13B5EA 0%, #0078C1 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px',
            color: '#fff',
            fontSize: 28,
            fontWeight: 700,
          }}
        >
          X
        </div>

        {status === 'processing' && (
          <>
            <Loader2
              size={36}
              style={{
                animation: 'spin 1s linear infinite',
                color: '#13B5EA',
                margin: '0 auto 16px',
                display: 'block',
              }}
            />
            <h2
              style={{
                margin: '0 0 8px',
                fontSize: 20,
                fontWeight: 600,
                color: 'var(--text-primary)',
              }}
            >
              Connecting to Xero
            </h2>
            <p
              style={{
                margin: 0,
                fontSize: 14,
                color: 'var(--text-muted)',
              }}
            >
              Exchanging authorization code and setting up your integration…
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle2
              size={48}
              style={{
                color: '#10b981',
                margin: '0 auto 16px',
                display: 'block',
              }}
            />
            <h2
              style={{
                margin: '0 0 8px',
                fontSize: 20,
                fontWeight: 600,
                color: 'var(--text-primary)',
              }}
            >
              Xero Connected!
            </h2>
            <p
              style={{
                margin: '0 0 16px',
                fontSize: 14,
                color: 'var(--text-muted)',
              }}
            >
              Your Xero account has been linked. Redirecting to settings…
            </p>
            <div
              style={{
                width: 120,
                height: 4,
                background: 'var(--bg-secondary)',
                borderRadius: 2,
                margin: '0 auto',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(90deg, #13B5EA, #0078C1)',
                  animation: 'shrink 2s linear forwards',
                }}
              />
            </div>
            <style>{`
              @keyframes shrink { from { width: 100%; } to { width: 0%; } }
            `}</style>
          </>
        )}

        {status === 'error' && (
          <>
            <XCircle
              size={48}
              style={{
                color: '#ef4444',
                margin: '0 auto 16px',
                display: 'block',
              }}
            />
            <h2
              style={{
                margin: '0 0 8px',
                fontSize: 20,
                fontWeight: 600,
                color: 'var(--text-primary)',
              }}
            >
              Connection Failed
            </h2>
            <p
              style={{
                margin: '0 0 24px',
                fontSize: 14,
                color: 'var(--text-muted)',
              }}
            >
              {errorMsg}
            </p>
            <button
              className="btn btn-primary"
              onClick={() => navigate('/org-settings?tab=integrations', { replace: true })}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
            >
              Back to Settings
            </button>
          </>
        )}
      </div>
    </div>
  );
}
